import { Link, useNavigate, useParams } from 'react-router-dom';
import PageGrid from '../../templates/PageGrid';
import ActionButton from '../../molecules/ActionButton';
import Tooltip from '../../atoms/Tooltip';
import { IoDocumentText, IoEye } from 'react-icons/io5';

type Operation = {
    id: string;
    operationCode: string;
    assignorName: string;
    assignorCnpj: string;
    status: string;
    totalDocuments: number;
    totalReceivables: number;
    totalAmount: number;
    processingDate: string;
    updatedDate: string;
    notes: string;
    canEdit: boolean;
    canRemove: boolean;
    canViewDetail: boolean;
    canDownload: boolean;
};

const API_URL = process.env.REACT_APP_CONVERT_XML_API_URL || '';
const API_KEY = process.env.REACT_APP_API_KEY || '';

const OperationPageList = () => {
    const navigate = useNavigate();
    const { page = '0', pageSize = '12' } = useParams();

    const url = `${API_URL}/operations`;

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return 'bg-green-100 text-green-800';
            case 'processing': return 'bg-yellow-100 text-yellow-800';
            case 'failed': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'completed': return 'Concluído';
            case 'processing': return 'Processando';
            case 'failed': return 'Falhou';
            default: return status;
        }
    };


    const renderCards = (operations: Operation[]) => {
        if (!operations || operations.length === 0) {
            return (
                <div className="mt-8 text-center py-12">
                    <IoDocumentText className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma operação encontrada</h3>
                    <p className="mt-1 text-sm text-gray-500">Comece criando uma nova operação.</p>
                </div>
            );
        }

        return (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {operations.map((operation: Operation) => (
                    <div key={operation.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                        <div className="mb-4">
                            <Tooltip content={operation.operationCode} alignment="bottom">
                                <h3 className="text-base font-semibold text-gray-900 truncate">{operation.operationCode}</h3>
                            </Tooltip>
                            <p className="text-sm text-gray-600">{operation.assignorName}</p>
                        </div>

                        <div className="space-y-3 mb-4">
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Documentos:</span>
                                <span className="text-sm font-medium">{operation.totalDocuments || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Recebíveis:</span>
                                <span className="text-sm font-medium">{operation.totalReceivables || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Valor Total:</span>
                                <span className="text-sm font-medium text-green-600">{formatCurrency(operation.totalAmount || 0)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Processamento:</span>
                                <span className="text-sm font-medium">{formatDate(operation.processingDate)}</span>
                            </div>
                        </div>

                        {operation.notes && (
                            <p className="text-xs text-gray-500 mb-4 border-t pt-3">{operation.notes}</p>
                        )}

                        <div className="flex justify-between items-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(operation.status)}`}>
                                {getStatusLabel(operation.status)}
                            </span>
                            <div className="flex space-x-2">
                                {operation.canViewDetail && (
                                    <button
                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                        onClick={() => navigate(`/structuring/operation/view/${operation.id}`)}
                                    >
                                        <IoEye className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <>
            <PageGrid
                pageTitle="Operações"
                url={url}
                fetchHeaders={[{ key: 'x-api-key', value: API_KEY }]}
                breadcrumb={[{ label: 'Operações', url: '#' }]}
                renderCards={renderCards}
                page={parseInt(page)}
                pageSize={parseInt(pageSize)}
                onPageChange={(newPage) => navigate(`/structuring/operations/${newPage - 1}/${pageSize}`)}
                onItemsPerPageChange={(newPageSize) => navigate(`/structuring/operations/0/${newPageSize}`)}
                itemsLabel="operações"
            >
                {{
                    controlSlot: (
                        <Link to="/structuring/operation">
                            <ActionButton
                                label="Nova operação"
                                className="bg-blue-600 hover:bg-blue-700"
                            />
                        </Link>
                    ),
                    filterSlot: null,
                }}
            </PageGrid>
        </>
    );
};

export default OperationPageList;
